import { useState } from "react";
import { useTranslation } from "react-i18next";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import {
  Check,
  Store,
  Coffee,
  Stethoscope,
  Briefcase,
  Megaphone,
  Laptop,
  type LucideIcon,
} from "lucide-react";
import { SectionHeading } from "./SectionHeading";

type TileKey =
  | "retail"
  | "food_beverage"
  | "healthcare"
  | "consulting"
  | "marketing"
  | "technology";

// The six industries with tailored starter data at signup
// (backend/businesses/seeds_starter.py), each with its own tone.
// Class bundles carry light + dark variants.
const TILES: Array<{
  key: TileKey;
  icon: LucideIcon;
  /** Tag pill in the detail card. */
  pill: string;
  /** Selected-tile chrome. */
  selected: string;
  /** Bullet check icon color. */
  check: string;
}> = [
  {
    key: "retail",
    icon: Store,
    pill: "bg-emerald-50 text-emerald-700 ring-emerald-200 dark:bg-emerald-500/10 dark:text-emerald-300 dark:ring-emerald-500/30",
    selected:
      "ring-emerald-400 bg-emerald-50 text-emerald-900 dark:ring-emerald-400/60 dark:bg-emerald-500/10 dark:text-white",
    check: "text-emerald-600 dark:text-emerald-400",
  },
  {
    key: "food_beverage",
    icon: Coffee,
    pill: "bg-rose-50 text-rose-700 ring-rose-200 dark:bg-rose-500/10 dark:text-rose-300 dark:ring-rose-500/30",
    selected:
      "ring-rose-400 bg-rose-50 text-rose-900 dark:ring-rose-400/60 dark:bg-rose-500/10 dark:text-white",
    check: "text-rose-600 dark:text-rose-400",
  },
  {
    key: "healthcare",
    icon: Stethoscope,
    pill: "bg-sky-50 text-sky-700 ring-sky-200 dark:bg-sky-500/10 dark:text-sky-300 dark:ring-sky-500/30",
    selected:
      "ring-sky-400 bg-sky-50 text-sky-900 dark:ring-sky-400/60 dark:bg-sky-500/10 dark:text-white",
    check: "text-sky-600 dark:text-sky-400",
  },
  {
    key: "consulting",
    icon: Briefcase,
    pill: "bg-violet-50 text-violet-700 ring-violet-200 dark:bg-violet-500/10 dark:text-violet-300 dark:ring-violet-500/30",
    selected:
      "ring-violet-400 bg-violet-50 text-violet-900 dark:ring-violet-400/60 dark:bg-violet-500/10 dark:text-white",
    check: "text-violet-600 dark:text-violet-400",
  },
  {
    key: "marketing",
    icon: Megaphone,
    pill: "bg-fuchsia-50 text-fuchsia-700 ring-fuchsia-200 dark:bg-fuchsia-500/10 dark:text-fuchsia-300 dark:ring-fuchsia-500/30",
    selected:
      "ring-fuchsia-400 bg-fuchsia-50 text-fuchsia-900 dark:ring-fuchsia-400/60 dark:bg-fuchsia-500/10 dark:text-white",
    check: "text-fuchsia-600 dark:text-fuchsia-400",
  },
  {
    key: "technology",
    icon: Laptop,
    pill: "bg-cyan-50 text-cyan-700 ring-cyan-200 dark:bg-cyan-500/10 dark:text-cyan-300 dark:ring-cyan-500/30",
    selected:
      "ring-cyan-400 bg-cyan-50 text-cyan-900 dark:ring-cyan-400/60 dark:bg-cyan-500/10 dark:text-white",
    check: "text-cyan-600 dark:text-cyan-400",
  },
];

/** Interactive industries section: a grid with every business type; selecting
 * a tile reveals its pain-led detail card (tag, headline, body, bullets). */
export function Verticals() {
  const { t } = useTranslation();
  const prefersReduced = useReducedMotion();
  const [active, setActive] = useState<TileKey>("retail");
  const tile = TILES.find((x) => x.key === active) ?? TILES[0];
  const ActiveIcon = tile.icon;
  const bullets = t(`landing.verticals.tiles.${active}.bullets`, {
    returnObjects: true,
  }) as string[];

  return (
    <section
      id="verticals"
      className="relative scroll-mt-16 border-t border-slate-200/80 bg-white py-20 dark:border-white/5 dark:bg-slate-950 sm:py-28"
    >
      <div className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-8">
        <SectionHeading
          tag={t("landing.verticals.tag")}
          title={t("landing.verticals.title")}
          subtitle={t("landing.verticals.subtitle")}
        />

        {/* Industry selector grid */}
        <div
          role="tablist"
          aria-label={t("landing.verticals.tag")}
          className="grid grid-cols-2 gap-2.5 sm:grid-cols-3 lg:grid-cols-6"
        >
          {TILES.map(({ key, icon: Icon, selected }) => {
            const isActive = key === active;
            return (
              <button
                key={key}
                type="button"
                role="tab"
                aria-selected={isActive}
                onClick={() => setActive(key)}
                className={`flex flex-col items-center gap-2 rounded-2xl px-3 py-4 text-center text-xs font-semibold ring-1 transition-all ${
                  isActive
                    ? selected
                    : "bg-white text-slate-600 ring-slate-200 hover:bg-slate-50 hover:text-slate-900 dark:bg-white/[0.02] dark:text-slate-300 dark:ring-white/10 dark:hover:bg-white/5 dark:hover:text-white"
                }`}
              >
                <Icon size={18} />
                {t(`landing.verticals.tiles.${key}.title`)}
              </button>
            );
          })}
        </div>

        {/* Detail card */}
        <div className="mt-6 overflow-hidden rounded-3xl bg-white ring-1 ring-slate-200 shadow-sm dark:bg-white/[0.02] dark:ring-white/10 dark:shadow-none">
          <AnimatePresence mode="wait" initial={false}>
            <motion.div
              key={active}
              initial={prefersReduced ? { opacity: 1 } : { opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={prefersReduced ? { opacity: 1 } : { opacity: 0, y: -10 }}
              transition={{ duration: 0.22 }}
              className="p-6 sm:p-10"
            >
              <span
                className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-semibold ring-1 ${tile.pill}`}
              >
                <ActiveIcon size={13} />
                {t(`landing.verticals.tiles.${active}.title`)}
              </span>
              <h3 className="mt-4 max-w-2xl text-2xl font-bold leading-tight text-slate-900 dark:text-white sm:text-4xl">
                {t(`landing.verticals.tiles.${active}.headline`)}
              </h3>
              <p className="mt-3 max-w-2xl leading-relaxed text-slate-600 dark:text-slate-300">
                {t(`landing.verticals.tiles.${active}.body`)}
              </p>
              <ul className="mt-5 space-y-2.5">
                {bullets.map((b) => (
                  <li key={b} className="flex items-start gap-2.5 text-sm text-slate-700 dark:text-slate-200">
                    <Check size={16} className={`mt-0.5 flex-none ${tile.check}`} />
                    <span>{b}</span>
                  </li>
                ))}
              </ul>
            </motion.div>
          </AnimatePresence>
        </div>

        <p className="mx-auto mt-6 max-w-md text-center text-xs text-slate-500">
          {t("landing.verticals.footnote")}
        </p>
      </div>
    </section>
  );
}
